import { AsyncPipe, NgIf } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnChanges, OnInit, Signal, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProfessionalImmersionExperience } from '@lx/shared/data-access/products';
import { Amount } from '@lx/shared/data-access/types';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { BehaviorSubject, Observable } from 'rxjs';
import { GoogleAnalyticsEventEnum } from '../../google-analytics';
import { QuoteOutputProduct, QuoteStore, QuoteToolLiteStore } from '../../store';
import { QTLiteStoreSignalsAdapterService } from '../../services/qt-lite-store-signals-adapter.service';
import { OperationKey } from '../../constants';
import { CardContainerComponent } from '@lx/quote-tool/lite/shared/ui/components';

@Component({
  standalone: true,
  selector: 'lx-professional-immersion-experience',
  templateUrl: './professional-immersion-experience.component.html',
  styleUrls: ['./professional-immersion-experience.component.scss'],
  imports: [CardContainerComponent, NgIf, AsyncPipe],
})
export class ProfessionalImmersionExperienceComponent implements OnChanges, OnInit {
  @Input() professionalImmersionExperience: ProfessionalImmersionExperience | undefined;

  protected selectedProfessionalImmersionExperienceCode = '';
  protected currency: Signal<string | undefined>;
  protected price: Amount[] | undefined;
  protected endPrice: Amount[] | undefined;
  protected discount: string | undefined;
  protected loading: Signal<boolean | undefined>;
  protected dataLayerEventName$: Observable<GoogleAnalyticsEventEnum> | undefined;
  private dataLayerEventName = new BehaviorSubject<GoogleAnalyticsEventEnum>(undefined);

  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly qtLiteStoreSignalsAdapterService = inject(QTLiteStoreSignalsAdapterService);
  private readonly quoteStore = inject(QuoteStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loadingService = inject(LoadingService);

  ngOnChanges({ professionalImmersionExperience }: SimpleChanges) {
    if (professionalImmersionExperience?.currentValue) {
      this.endPrice = professionalImmersionExperience.currentValue.multiCurrencyPrice;
    }
  }

  ngOnInit(): void {
    this.currency = this.qtLiteStoreSignalsAdapterService.selectedCurrency;
    this.handleSelectedProfessionalImmersionExperienceCode();
    this.quoteStore.professionalImmersionExperience$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((quotedPie: QuoteOutputProduct) => {
      this.setPropertiesFromQuotedProfessionalImmersionExperience(quotedPie);
    });

    this.dataLayerEventName$ = this.dataLayerEventName.asObservable();
    this.loading = this.loadingService.areAllOperationsLoading([OperationKey.prices, OperationKey.extras]);
  }

  toggleProfessionalImmersionExperience(selectedProfessionalImmersionExperienceCode: string) {
    if (!this.selectedProfessionalImmersionExperienceCode) {
      this.quoteToolLiteStore.updateState({ selectedProfessionalImmersionExperienceCode });
      this.dataLayerEventName.next(GoogleAnalyticsEventEnum.QuoteProfessionalImmersionExperienceAdd);
    } else {
      this.quoteToolLiteStore.updateState({ selectedProfessionalImmersionExperienceCode: undefined });
      this.dataLayerEventName.next(GoogleAnalyticsEventEnum.QuoteProfessionalImmersionExperienceRemove);
    }
  }

  private handleSelectedProfessionalImmersionExperienceCode() {
    this.quoteToolLiteStore.selectedProfessionalImmersionExperienceCode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedProfessionalImmersionExperienceCode) => {
        this.selectedProfessionalImmersionExperienceCode = selectedProfessionalImmersionExperienceCode;
      });
  }

  private setPropertiesFromQuotedProfessionalImmersionExperience(quotedProfessionalImmersionExperience: QuoteOutputProduct): void {
    const { code, endPrice, price, discountTotalAsPercentage } = quotedProfessionalImmersionExperience || {};

    this.selectedProfessionalImmersionExperienceCode = code;
    this.endPrice = endPrice || this.professionalImmersionExperience?.multiCurrencyPrice;
    this.price = price || this.endPrice;
    this.discount = discountTotalAsPercentage;
  }
}
